import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from 'react';
import {
  RushdMembershipStatus,
  RushdPricingQuote,
  RushdCheckoutSession,
  RushdBillingPortalSession,
  RushdMembershipContextType,
} from '../types/rushd';
import { rushdApiService } from '../services/rushdService';
import { useAuthContext } from '../lib/AuthProvider';
import { useCurrency } from './CurrencyContext';
import { useToast } from '../hooks/use-toast';

// Error types for better UX
type ErrorType = 'NETWORK_ERROR' | 'AUTH_ERROR' | 'VALIDATION_ERROR' | 'UNKNOWN_ERROR';

interface ErrorState {
  type: ErrorType;
  message: string;
  retryable: boolean;
  timestamp: Date;
}

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 5000,
};

// Cache configuration
const CACHE_CONFIG = {
  membershipTtl: 2 * 60 * 1000, // 2 minutes
  pricingTtl: 30 * 1000, // 30 seconds
};

/**
 * Enhanced error categorization
 */
function categorizeError(error: any): ErrorState {
  const timestamp = new Date();
  
  if (error?.name === 'NetworkError' || error?.code === 'NETWORK_ERROR') {
    return {
      type: 'NETWORK_ERROR',
      message: 'Unable to connect to server. Please check your internet connection.',
      retryable: true,
      timestamp,
    };
  }
  
  if (error?.status === 401 || error?.message?.includes('unauthorized')) {
    return {
      type: 'AUTH_ERROR',
      message: 'Please sign in again to continue.',
      retryable: false,
      timestamp,
    };
  }
  
  if (error?.status === 400 || error?.message?.includes('validation')) {
    return {
      type: 'VALIDATION_ERROR',
      message: error?.message || 'Invalid request. Please try again.',
      retryable: false,
      timestamp,
    };
  }
  
  return {
    type: 'UNKNOWN_ERROR',
    message: error?.message || 'An unexpected error occurred. Please try again.',
    retryable: true,
    timestamp,
  };
}

/**
 * Retry logic with exponential backoff
 */
async function retryOperation<T>(
  operation: () => Promise<T>,
  config = RETRY_CONFIG
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      const errorState = categorizeError(error);
      
      // Don't retry non-retryable errors
      if (!errorState.retryable || attempt === config.maxRetries) {
        throw error;
      }
      
      // Calculate delay with exponential backoff and jitter
      const delay = Math.min(
        config.baseDelay * Math.pow(2, attempt) + Math.random() * 200,
        config.maxDelay
      );
      
      console.warn(`Operation failed (attempt ${attempt + 1}/${config.maxRetries + 1}), retrying in ${delay}ms:`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

const RushdMembershipContext = createContext<RushdMembershipContextType | undefined>(undefined);

interface RushdMembershipProviderProps {
  children: ReactNode;
}

export function RushdMembershipProvider({ children }: RushdMembershipProviderProps) {
  const [membershipStatus, setMembershipStatus] = useState<RushdMembershipStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Get user's selected currency
  const { selectedCurrency } = useCurrency();
  
  // Cache management
  const membershipCacheRef = useRef<{ data: RushdMembershipStatus | null; timestamp: number }>({ data: null, timestamp: 0 });
  const pricingCacheRef = useRef<Map<string, { data: RushdPricingQuote; timestamp: number }>>(new Map());
  
  // Circuit breaker state
  const circuitBreakerRef = useRef<{ isOpen: boolean; lastFailure: number; failureCount: number }>({
    isOpen: false,
    lastFailure: 0,
    failureCount: 0,
  });
  
  const { user } = useAuthContext();
  const { toast } = useToast();
  
  /**
   * Circuit breaker logic
   */
  const checkCircuitBreaker = useCallback(() => {
    const circuitBreaker = circuitBreakerRef.current;
    const now = Date.now();
    
    // Reset circuit breaker after 5 minutes
    if (circuitBreaker.isOpen && (now - circuitBreaker.lastFailure) > 5 * 60 * 1000) {
      circuitBreaker.isOpen = false;
      circuitBreaker.failureCount = 0;
      console.log('Circuit breaker reset for Rush\'d membership service');
    }
    
    return !circuitBreaker.isOpen;
  }, []);
  
  /**
   * Record circuit breaker failure
   */
  const recordFailure = useCallback(() => {
    const circuitBreaker = circuitBreakerRef.current;
    circuitBreaker.failureCount += 1;
    circuitBreaker.lastFailure = Date.now();
    
    // Open circuit breaker after 5 consecutive failures
    if (circuitBreaker.failureCount >= 5) {
      circuitBreaker.isOpen = true;
      console.warn('Circuit breaker opened for Rush\'d membership service due to repeated failures');
    }
  }, []);
  
  /**
   * Check if cached data is still valid
   */
  const isCacheValid = useCallback((timestamp: number, ttl: number) => {
    return (Date.now() - timestamp) < ttl;
  }, []);
  
  /**
   * Get membership status with caching
   */
  const getCachedMembershipStatus = useCallback(() => {
    const cache = membershipCacheRef.current;
    return isCacheValid(cache.timestamp, CACHE_CONFIG.membershipTtl) ? cache.data : null;
  }, [isCacheValid]);
  
  /**
   * Update membership cache
   */
  const updateMembershipCache = useCallback((data: RushdMembershipStatus | null) => {
    membershipCacheRef.current = {
      data,
      timestamp: Date.now(),
    };
  }, []);
  
  /**
   * Refresh membership status from API with enhanced error handling
   */
  const refreshMembership = useCallback(async (forceRefresh = false) => {
    if (!user?.uid) {
      const defaultStatus = { isMember: false, isActive: false };
      setMembershipStatus(defaultStatus);
      updateMembershipCache(defaultStatus);
      return;
    }
    
    // Check cache first unless forced refresh
    if (!forceRefresh) {
      const cachedStatus = getCachedMembershipStatus();
      if (cachedStatus) {
        setMembershipStatus(cachedStatus);
        return;
      }
    }
    
    // Check circuit breaker
    if (!checkCircuitBreaker()) {
      const errorState: ErrorState = {
        type: 'NETWORK_ERROR',
        message: 'Service temporarily unavailable. Please try again later.',
        retryable: true,
        timestamp: new Date(),
      };
      setError(errorState);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const status = await retryOperation(async () => {
        return await rushdApiService.getMembershipStatusFromFirebase(user.uid);
      });
      
      setMembershipStatus(status);
      updateMembershipCache(status);
      setRetryCount(0);
      
      // Reset circuit breaker on success
      circuitBreakerRef.current.failureCount = 0;
      
    } catch (err) {
      const errorState = categorizeError(err);
      setError(errorState);
      recordFailure();
      
      console.error('Error refreshing membership:', err, {
        userId: user.uid,
        retryCount,
        circuitBreakerState: circuitBreakerRef.current,
      });
      
      // Use cached data if available during errors
      const cachedStatus = membershipCacheRef.current.data;
      if (cachedStatus) {
        console.warn('Using cached membership status due to error');
        setMembershipStatus(cachedStatus);
        
        // Show warning toast for stale data
        toast({
          title: 'Using Cached Data',
          description: 'Unable to refresh membership status. Showing last known status.',
          variant: 'destructive',
        });
      } else {
        // Set safe default when no cache available
        const defaultStatus = { isMember: false, isActive: false };
        setMembershipStatus(defaultStatus);
        updateMembershipCache(defaultStatus);
      }
      
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, getCachedMembershipStatus, updateMembershipCache, checkCircuitBreaker, recordFailure, retryCount, toast]);
  
  /**
   * Create Stripe Checkout session for subscription with enhanced error handling
   */
  const createCheckoutSession = useCallback(async (
    plan: 'monthly' | 'yearly',
    successUrl: string,
    cancelUrl: string
  ): Promise<RushdCheckoutSession> => {
    // Input validation
    if (!user?.uid || !user?.email) {
      const error = new Error('User must be signed in to subscribe to Rush\'d');
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to subscribe to Rush\'d.',
        variant: 'destructive',
      });
      throw error;
    }
    
    if (!plan || !['monthly', 'yearly'].includes(plan)) {
      const error = new Error('Invalid subscription plan selected');
      toast({
        title: 'Invalid Plan',
        description: 'Please select a valid subscription plan.',
        variant: 'destructive',
      });
      throw error;
    }
    
    if (!successUrl || !cancelUrl) {
      const error = new Error('Invalid redirect URLs provided');
      toast({
        title: 'Configuration Error',
        description: 'Invalid checkout configuration. Please contact support.',
        variant: 'destructive',
      });
      throw error;
    }
    
    try {
      const session = await retryOperation(async () => {
        // Convert currency to lowercase for backend
        const currency = selectedCurrency.toLowerCase() as 'usd' | 'cad' | 'gbp';
        return await rushdApiService.createCheckoutSession(
          plan,
          currency,
          user.uid,
          user.email,
          successUrl,
          cancelUrl
        );
      });
      
      // Validate session response
      if (!session?.url) {
        throw new Error('Invalid checkout session response');
      }
      
      return session;
    } catch (err) {
      const errorState = categorizeError(err);
      
      toast({
        title: 'Checkout Error',
        description: errorState.message,
        variant: 'destructive',
        action: errorState.retryable ? {
          altText: 'Retry',
          onClick: () => createCheckoutSession(plan, successUrl, cancelUrl),
        } : undefined,
      });
      
      console.error('Error creating checkout session:', err, {
        plan,
        userId: user.uid,
        userEmail: user.email,
      });
      
      throw err;
    }
  }, [user?.uid, user?.email, toast]);
  
  /**
   * Create Stripe Billing Portal session with enhanced validation
   */
  const createBillingPortalSession = useCallback(async (
    returnUrl: string
  ): Promise<RushdBillingPortalSession> => {
    // Input validation
    if (!user?.uid) {
      const error = new Error('User must be signed in to access billing portal');
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to access your billing portal.',
        variant: 'destructive',
      });
      throw error;
    }
    
    if (!returnUrl) {
      const error = new Error('Invalid return URL provided');
      toast({
        title: 'Configuration Error',
        description: 'Invalid billing portal configuration. Please contact support.',
        variant: 'destructive',
      });
      throw error;
    }
    
    // Check membership status with fresh data if needed
    if (!membershipStatus?.isMember) {
      // Try to refresh membership status first
      await refreshMembership(true);
      
      // Check again after refresh
      if (!membershipStatus?.isMember) {
        const error = new Error('User must have a Rush\'d membership to access billing portal');
        toast({
          title: 'Membership Required',
          description: 'You must be a Rush\'d member to access the billing portal.',
          variant: 'destructive',
        });
        throw error;
      }
    }
    
    try {
      const session = await retryOperation(async () => {
        return await rushdApiService.createBillingPortalSession(
          user.uid,
          returnUrl
        );
      });
      
      // Validate session response
      if (!session?.url) {
        throw new Error('Invalid billing portal session response');
      }
      
      return session;
    } catch (err) {
      const errorState = categorizeError(err);
      
      toast({
        title: 'Billing Portal Error',
        description: errorState.message,
        variant: 'destructive',
        action: errorState.retryable ? {
          altText: 'Retry',
          onClick: () => createBillingPortalSession(returnUrl),
        } : undefined,
      });
      
      console.error('Error creating billing portal session:', err, {
        userId: user.uid,
        returnUrl,
        membershipStatus,
      });
      
      throw err;
    }
  }, [user?.uid, membershipStatus?.isMember, refreshMembership, toast]);
  
  /**
   * Get Express shipping pricing quote with caching and enhanced error handling
   */
  const getExpressPricingQuote = useCallback(async (
    cartItems: any[],
    shippingAddress: any,
    cartSubtotal: number
  ): Promise<RushdPricingQuote> => {
    // Input validation
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      console.warn('Invalid cart items provided for pricing quote');
      return {
        regularPrice: 35.00,
        isMember: false,
        isEligible: false,
        reason: 'Cart is empty',
      };
    }
    
    if (typeof cartSubtotal !== 'number' || cartSubtotal < 0) {
      console.warn('Invalid cart subtotal provided for pricing quote');
      return {
        regularPrice: 35.00,
        isMember: false,
        isEligible: false,
        reason: 'Invalid cart total',
      };
    }
    
    // Create cache key
    const cacheKey = `${user?.uid || 'guest'}-${cartSubtotal}-${JSON.stringify(shippingAddress)}`;
    
    // Check cache first
    const cachedQuote = pricingCacheRef.current.get(cacheKey);
    if (cachedQuote && isCacheValid(cachedQuote.timestamp, CACHE_CONFIG.pricingTtl)) {
      return cachedQuote.data;
    }
    
    try {
      const quote = await retryOperation(async () => {
        return await rushdApiService.getExpressPricingQuote(
          cartItems,
          shippingAddress,
          cartSubtotal,
          user?.uid
        );
      });
      
      // Validate quote response
      if (!quote || typeof quote.regularPrice !== 'number') {
        throw new Error('Invalid pricing quote response');
      }
      
      // Cache the result
      pricingCacheRef.current.set(cacheKey, {
        data: quote,
        timestamp: Date.now(),
      });
      
      return quote;
    } catch (err) {
      console.error('Error getting Express pricing quote:', err, {
        userId: user?.uid,
        cartSubtotal,
        itemCount: cartItems.length,
      });
      
      // Return fallback quote with appropriate messaging
      const errorState = categorizeError(err);
      return {
        regularPrice: 35.00,
        isMember: false,
        isEligible: false,
        reason: errorState.type === 'NETWORK_ERROR' 
          ? 'Unable to fetch current pricing. Using standard rate.'
          : 'Pricing temporarily unavailable. Using standard rate.',
      };
    }
  }, [user?.uid, isCacheValid]);
  
  /**
   * Manual retry function for users
   */
  const retryLastOperation = useCallback(async () => {
    if (error?.retryable) {
      setError(null);
      await refreshMembership(true);
    }
  }, [error?.retryable, refreshMembership]);
  
  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
    setRetryCount(0);
  }, []);
  
  // Load membership status when user changes
  useEffect(() => {
    refreshMembership();
  }, [refreshMembership]);
  
  // Listen for visibility changes to refresh membership (catches webhook updates)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.uid) {
        // Refresh membership when user comes back to the tab
        refreshMembership();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshMembership, user?.uid]);
  
  // Auto-refresh membership status every 5 minutes for webhook changes
  useEffect(() => {
    if (!user?.uid) return;
    
    const interval = setInterval(() => {
      refreshMembership();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [refreshMembership, user?.uid]);
  
  const value: RushdMembershipContextType = {
    membershipStatus,
    loading,
    error: error?.message || null,
    errorState: error,
    refreshMembership,
    createCheckoutSession,
    createBillingPortalSession,
    getExpressPricingQuote,
    retryLastOperation,
    clearError,
    retryCount,
  };
  
  return (
    <RushdMembershipContext.Provider value={value}>
      {children}
    </RushdMembershipContext.Provider>
  );
}

export function useRushdMembership() {
  const context = useContext(RushdMembershipContext);
  if (context === undefined) {
    throw new Error('useRushdMembership must be used within a RushdMembershipProvider');
  }
  return context;
}

// Convenience hook for checking membership status
export function useRushdMembershipStatus() {
  const { membershipStatus, loading, errorState } = useRushdMembership();
  
  return {
    isMember: membershipStatus?.isMember || false,
    isActive: membershipStatus?.isActive || false,
    plan: membershipStatus?.plan,
    loading,
    membershipStatus,
    hasError: !!errorState,
    isStale: errorState?.type === 'NETWORK_ERROR' && !!membershipStatus,
  };
}

// Hook for error recovery
export function useRushdErrorRecovery() {
  const { errorState, retryLastOperation, clearError, retryCount } = useRushdMembership();
  
  return {
    error: errorState,
    canRetry: errorState?.retryable || false,
    retryCount,
    retry: retryLastOperation,
    dismiss: clearError,
  };
}